import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';
import {Button, Checkbox, Form, Input, Popconfirm, Select, Space} from 'antd';
import {observer} from 'mobx-react-lite';
import {EmployeeState} from '../../../store/users/employees/EmployeeState';
import {Shell} from '../../../components/Shell';
import {AppRoutePaths} from '../../../routing/Routes';
import {Spinner} from '../../../components/ui/Spinner';
import {countryPrefixes} from '../../../components/form/PhoneInput';
import {UserRole} from '../../../../../../PRMGApiClient';
import classNames from 'classnames';
import i18n from '../../../i18n/i18n';

const options = Object.values(UserRole).map((role) => {
  return {
    value: role,
    label: role,
  };
});
export const EmployeeEditView: React.FC = observer((): JSX.Element => {
  const {t} = useTranslation();
  const {employeeId} = useParams();
  const [state] = useState(() => new EmployeeState());
  const [isActive, setIsActive] = useState(true);
  const [form] = Form.useForm();

  useEffect(() => {
    employeeId && state.loadEmployee(parseInt(employeeId ?? '0'));
  }, [employeeId, state]);

  useEffect(() => {
    form.setFieldsValue(state.item);
  }, [form, state.item]);

  return (
    <>
      {state.isLoading && (
        <div className="absolute top-0 left-0 w-full h-full bg-white opacity-50 z-10 h-screen flex justify-center items-center skip-fade">
          <Spinner />
        </div>
      )}
      <Shell
        title={t(employeeId ? 'Edit Employee' : 'Add Employee')}
        backLink={employeeId ? AppRoutePaths.employee.url : AppRoutePaths.employees.url}
      >
        <Space direction="vertical" className="w-full">
          <Form
            form={form}
            onFinish={(values) => {
              state.save({
                ...values,
                userName: values.email,
                isActive: isActive,
                userRole: values.userRole,
              });
            }}
            layout="vertical"
          >
            <Form.Item className={'flex justify-end'}>
              <Space wrap>
                <Popconfirm
                  key="delete"
                  placement="bottomRight"
                  title={t('Are you sure you want to remove this employee?')}
                  onConfirm={() => state.deleteEmployee()}
                  okText={t('Yes')}
                  cancelText={t('No')}
                >
                  <Button size="large" className={classNames(employeeId ? 'visible' : 'invisible')}>
                    {t('Remove')}
                  </Button>
                </Popconfirm>{' '}
                <Button size="large" type="primary" htmlType="submit">
                  Save
                </Button>
              </Space>
            </Form.Item>
            <Form.Item label={t('Full name')} required>
              <Input.Group compact>
                <Form.Item
                  required
                  noStyle
                  name="firstName"
                  rules={[{required: true, message: String(t('First name is required.'))}]}
                >
                  <Input placeholder={t('First name')} style={{width: '50%'}} />
                </Form.Item>
                <Form.Item
                  required
                  noStyle
                  name="lastName"
                  rules={[{required: true, message: String(t('Last name is required.'))}]}
                >
                  <Input placeholder={t('Last name')} style={{width: '50%'}} />
                </Form.Item>
              </Input.Group>
            </Form.Item>

            <Form.Item
              name="phoneNumber"
              label={i18n.t('Phone number')}
              rules={[{required: true, message: 'Please input your phone number!'}]}
            >
              <Input
                addonBefore={
                  <Form.Item name={'phoneNumberPrefix'} noStyle>
                    <Select
                      showSearch
                      placeholder="Select country"
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                      options={countryPrefixes}
                      style={{width: '200px'}}
                    />
                  </Form.Item>
                }
              />
            </Form.Item>

            {state.item.id ? (
              <>
                {' '}
                <Form.Item name={'id'} style={{display: 'none'}}>
                  <Input type="text" />
                </Form.Item>
              </>
            ) : (
              <>
                <Form.Item
                  required
                  name="email"
                  label={t('Email')}
                  rules={[{required: true, message: String(t('E-mail is required.'))}]}
                >
                  <Input placeholder={t('E-mail')} />
                </Form.Item>
                <Form.Item
                  required
                  name="password"
                  label={t('Password')}
                  rules={[{required: true, message: String(t('Password is required.'))}]}
                >
                  <Input placeholder={t('Password')} />
                </Form.Item>
              </>
            )}
            <Form.Item name="userRole" label="Role" rules={[{required: true}]}>
              <Select placeholder="Select a option and change input text above" allowClear options={options} />
            </Form.Item>
            <Form.Item>
              <Checkbox checked={isActive} onChange={() => setIsActive(!isActive)}>
                {t('Employee Active')}
              </Checkbox>
            </Form.Item>
          </Form>
        </Space>
      </Shell>
    </>
  );
});
